import type { SampleEvent } from "../types/contract";

/*
 *
 * NEVER TEST `kind === 'points'` DIRECTLY. `kind` is the event id's PREFIX, not a closed enum bulk
 * ingest mints `bulk:<session>:<uid>`, so a bulk-ingested POINT set is `kind: 'bulk'`.
 */
export const isPointShaped = (
  e: Pick<SampleEvent, "kind"> & { geometry_kind?: "point" | "polygon" },
): boolean =>
  e.geometry_kind ? e.geometry_kind === "point" : e.kind !== "zones";

// A null `sample_date` marks an "inline-attributed" event that BE-Light serves but the view ignores
// pinning one writes a dangling pointer the server drops, so it must never be offered.
export const switchableEvents = (events: SampleEvent[]): SampleEvent[] =>
  events.filter(
    (e) => e.has_results && isPointShaped(e) && e.sample_date != null,
  );

// events that actually carry lab results — only these can be a field's current campaign
export const resultsEvents = (events: SampleEvent[]): SampleEvent[] =>
  events.filter((e) => e.has_results);

// the server's designated current campaign (authoritative — the view resolves pin-else-newest)
export const currentEvent = (events: SampleEvent[]): SampleEvent | undefined =>
  events.find((e) => e.is_current);

// the campaign that WOULD be current on date order alone (newest switchable by sample_date)
export function newestResultsEvent(
  events: SampleEvent[],
): SampleEvent | undefined {
  const cands = switchableEvents(events);
  if (!cands.length) return undefined;
  // `created_at` is UPLOAD order, not sampling order — only a last-resort tiebreaker. NB the server's view breaks
  // sample_date ties on `last_ingest_at`, which we are not served, so a TIE is never treated as an override below.
  return [...cands].sort(
    (a, b) =>
      (b.sample_date ?? "").localeCompare(a.sample_date ?? "") ||
      (b.created_at ?? "").localeCompare(a.created_at ?? ""),
  )[0];
}

export interface PinState {
  current?: SampleEvent; // what the server says is current
  newest?: SampleEvent; // what date order alone would pick
  /*
   *
   * Requires a strict date advantage: on EQUAL `sample_date` the server breaks the tie on
   * `last_ingest_at` (DB view 046), which we are not served, so our ordering can disagree with the
   * server's for reasons that have nothing to do with a pin — claiming "pinned" there produces a
   * banner whose "use the newest" button is a no-op and never clears. Same rule as
   * `oldUploadReason`, so the two can't contradict.
   */
  overriding: boolean;
}

export function pinState(events: SampleEvent[] | undefined): PinState {
  const evs = events ?? [];
  const current = currentEvent(evs);
  const newest = newestResultsEvent(evs);
  // Only claim an override when both are known, they differ, AND the newest is STRICTLY newer by date — never on
  // a tie (see the note above) and never when the current campaign has no date to compare against.
  const overriding =
    !!current &&
    !!newest &&
    current.event_id !== newest.event_id &&
    !!newest.sample_date &&
    !!current.sample_date &&
    newest.sample_date > current.sample_date;
  return { current, newest, overriding };
}

// The "(current)" / "(old)" / "(latest)" suffix a picker row shows. `is_current` is authoritative when served
// the positional "(latest)" is the pre-contract fallback for the first row. Centralized so the two pickers can't
// drift (they carried identical copies).
export function eventStatusLabel(ev: SampleEvent, index: number): string {
  if (ev.is_current) return " (current)";
  if (ev.is_current === false && ev.has_results) return " (old)";
  return index === 0 ? " (latest)" : "";
}

// There are TWO reasons and they need different messages: it genuinely predates the current set, OR
// it is actually NEWER and a PIN is holding an older campaign current.
export function oldUploadReason(
  savedDate: string | null | undefined,
  current: SampleEvent | undefined,
): "pinned" | "predates" {
  const cur = current?.sample_date;
  // newer-than-current can only be "old" because a pin outranks it; anything else genuinely predates.
  // Unknown dates fall back to "predates" — the long-standing, less alarming message.
  if (savedDate && cur && savedDate > cur) return "pinned";
  return "predates";
}

// friendly, timezone-safe campaign date ("Mar 11, 2026") — built from the ISO parts, never
// Date-parsed (a bare "" parsed as UTC midnight renders as the prior day in a negative-offset
// zone).
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
export function fmtEventDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso.slice(0, 10);
  return `${MONTHS[Number(m[2]) - 1] ?? m[2]} ${Number(m[3])}, ${m[1]}`;
}
